import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useMyCarDueDatesApi } from "../../http/dueDates/api";
import Button from "../ui/Button";
import DatePicker from "../../ui/DatePicker";
import FileUploader from "../ui/FileUploader";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import ModalDialog from "../ui/ModalDialog";

type RenewDueDateDialogProps = {
  dueDateId: string;
  carId: string;
  onClose: (reset: boolean) => void;
};

export interface RenewFormBody {
  date?: string;
  files?: File[];
}

const RenewDueDatesDialog: FC<RenewDueDateDialogProps> = ({ dueDateId, carId, onClose }: RenewDueDateDialogProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHelpDate, setShowHelpDate] = useState(false);
  const [showHelpAttachments, setShowHelpAttachments] = useState(false);
  const [showMissingDateWarning, setShowMissingDateWarning] = useState<RenewFormBody | undefined>(undefined);
  const dueDateApi = useMyCarDueDatesApi();

  const onSubmit = async (formData: RenewFormBody) => {
    setIsSubmitting(true);
    try {
      await dueDateApi.renewDueDates({ date: formData.date, files: formData.files }, dueDateId, carId);
      dispatch(showSuccessNotification(i18n.t));
      onClose(true);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog onClose={() => onClose(false)} title={i18n.t.dueDates.renewSingular}>
      <Form<RenewFormBody>
        onSubmit={() => async (f) => {
          if (f.date) {
            await onSubmit(f);
          } else {
            setShowMissingDateWarning(f);
          }
        }}
        submitText={i18n.t.common.save}
        disabled={isSubmitting}
        secondaryButton={
          <Button
            type="secondary"
            buttonProps={{
              onClick: async () => onClose(false),
            }}
          >
            {i18n.t.common.cancel}
          </Button>
        }
      >
        {({ control, watch, setValue }) => {
          return (
            <FormFieldsContainer>
              <Controller
                name="date"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value) {
                      const d = new Date(value!);
                      if (d.getFullYear() < 1000) {
                        return i18n.t.common.invalidDate;
                      }
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField
                    label={i18n.t.common.date}
                    error={fieldState.error}
                    isOptional
                    showHelp={{
                      type: "info",
                      onOpen: () => setShowHelpDate(true),
                    }}
                  >
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => !!v && setValue("date", v)}
                        value={watch("date")}
                        isInvalid={isInvalid}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField
                label={i18n.t.common.attachments}
                isOptional
                showHelp={{
                  type: "info",
                  onOpen: () => setShowHelpAttachments(true),
                }}
              >
                <FileUploader
                  allowMultiSelection
                  isSubmitting={isSubmitting}
                  disabled={isSubmitting}
                  onChange={(f) => setValue("files", f)}
                />
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>

      {!!showHelpDate && (
        <ModalDialog
          title={i18n.t.common.renewalDate}
          onClose={() => setShowHelpDate(false)}
          actions={[
            {
              title: i18n.t.common.close,
              onClick: () => setShowHelpDate(false),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.t.common.dueDateHelpDialog}
        </ModalDialog>
      )}

      {!!showHelpAttachments && (
        <ModalDialog
          title={i18n.t.common.attachments}
          onClose={() => setShowHelpAttachments(false)}
          actions={[
            {
              title: i18n.t.common.close,
              onClick: () => setShowHelpAttachments(false),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.t.common.attachmentsHelpDialog}
        </ModalDialog>
      )}

      {!!showMissingDateWarning && (
        <ModalDialog
          title={i18n.t.common.renewalDate}
          onClose={() => setShowMissingDateWarning(undefined)}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setShowMissingDateWarning(undefined),
            },
            {
              title: i18n.t.common.continue,
              onClick: async () => {
                await onSubmit(showMissingDateWarning);
              },
            },
          ]}
        >
          {i18n.t.common.missingRenewalDate}
        </ModalDialog>
      )}
    </ModalDialog>
  );
};

export default RenewDueDatesDialog;
